import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "@/components/ui/use-toast";
import { checkDisposition, Disposition, suggestDispositionKey } from "@/services/dispositionService";
import { useEffect, useState } from "react";

// Edits one disposition of the assistant's list in the form. Nothing is written until the
// assistant is saved.
interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Set to change one; leave empty to add a new one. */
  editing?: Disposition | null;
  /** Fills in a new one, e.g. a suggestion from the agent's prompt. Ignored when editing. */
  draft?: Disposition | null;
  /** The keys already in this assistant's list, so a new key cannot repeat one. */
  existingKeys: string[];
  onSaved: (disposition: Disposition) => void;
}

export default function DispositionEditor({
  open,
  onOpenChange,
  editing,
  draft,
  existingKeys,
  onSaved,
}: Props) {
  const [label, setLabel] = useState("");
  const [key, setKey] = useState("");
  const [keyEdited, setKeyEdited] = useState(false);
  const [description, setDescription] = useState("");

  useEffect(() => {
    if (!open) return;
    const source = editing ?? draft;
    setLabel(source?.label ?? "");
    setKey(source?.key ?? "");
    // A drafted key stays as suggested while the label is edited; the person can still change it.
    setKeyEdited(!!source);
    setDescription(source?.description ?? "");
  }, [open, editing, draft]);

  const input = { key, label, description };
  // An edited one keeps its key, so it is only checked against the others.
  const problem = checkDisposition(input, editing ? [] : existingKeys);

  const save = () => {
    if (problem) {
      toast({ title: "Cannot save yet", description: problem, variant: "destructive" });
      return;
    }
    onSaved({ key, label: label.trim(), description: description.trim() });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{editing ? "Edit disposition" : "Add a disposition"}</DialogTitle>
          <DialogDescription>
            After each call, Voxi picks the disposition that fits best from this assistant's list.
            The description is what it reads, so say when it applies and what does not count. The
            key is what your system matches on, so it cannot change once added.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1">
              <Label htmlFor="disposition-label">Label</Label>
              <Input
                id="disposition-label"
                value={label}
                onChange={(e) => {
                  setLabel(e.target.value);
                  if (!keyEdited) setKey(suggestDispositionKey(e.target.value));
                }}
                placeholder="Not interested"
                className="bg-muted text-foreground border-input"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="disposition-key">Key</Label>
              <Input
                id="disposition-key"
                value={key}
                disabled={!!editing}
                onChange={(e) => {
                  setKey(e.target.value);
                  setKeyEdited(true);
                }}
                placeholder="not_interested"
                className="bg-muted text-foreground border-input font-mono"
              />
            </div>
          </div>
          <div className="space-y-1">
            <Label htmlFor="disposition-description">Description</Label>
            <Textarea
              id="disposition-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="When this applies, and what does not count."
              className="min-h-24 bg-muted text-foreground border-input"
            />
          </div>
          {problem && (label || key || description) && (
            <p className="text-sm text-amber-600 dark:text-amber-500">{problem}</p>
          )}
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button type="button" onClick={save}>
            {editing ? "Save changes" : "Add disposition"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
